import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeftIcon, CalendarIcon, CurrencyDollarIcon, RefreshIcon } from "@heroicons/react/outline";
import { toast } from "react-toastify";
import PropertyContextSelector, { propertyCode } from "../../../components/PropertyContextSelector";
import { getCurrencyPricing, getCurrencySettings, getProperties } from "../../../apis/channelManager";

const localDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const defaultDates = () => {
  const start = new Date();
  const end = new Date(start);
  end.setFullYear(end.getFullYear() + 1);
  return { start_date: localDate(start), end_date: localDate(end) };
};

const money = (value, currency) => {
  if (value === null || value === undefined || value === "") return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "NGN",
      maximumFractionDigits: 2,
    }).format(Number(value));
  } catch {
    return `${Number(value).toLocaleString()} ${currency || ""}`.trim();
  }
};

const CurrencyPricingPage = () => {
  const [properties, setProperties] = useState([]);
  const [selectedCode, setSelectedCode] = useState("");
  const [settings, setSettings] = useState({ currencies: [], currency_code: "NGN" });
  const [targetCurrency, setTargetCurrency] = useState("");
  const [filters, setFilters] = useState(defaultDates);
  const [appliedFilters, setAppliedFilters] = useState(defaultDates);
  const [pricing, setPricing] = useState({ rates: [], meta: {}, property_currency: "NGN", target_currency: "NGN" });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadPricing = async (code, target, range, page = 1) => {
    if (!code) return;
    const data = await getCurrencyPricing(code, {
      target_currency: target,
      start_date: range.start_date,
      end_date: range.end_date,
      page,
      per_page: 25,
    });
    setPricing(data || { rates: [], meta: {} });
  };

  useEffect(() => {
    let active = true;
    getProperties().then(async (payload) => {
      const list = payload?.resorts || payload || [];
      if (!active) return;
      setProperties(Array.isArray(list) ? list : []);
      if (list?.[0]) setSelectedCode(propertyCode(list[0]));
    }).catch(() => toast.error("Unable to load properties"));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!selectedCode) return;
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const data = await getCurrencySettings(selectedCode);
        const nextSettings = data || { currencies: [], currency_code: "NGN" };
        const nextTarget = nextSettings.currency_code || "NGN";
        if (!active) return;
        setSettings(nextSettings);
        setTargetCurrency(nextTarget);
        const nextRange = defaultDates();
        setFilters(nextRange);
        setAppliedFilters(nextRange);
        await loadPricing(selectedCode, nextTarget, nextRange, 1);
      } catch (error) {
        if (active) toast.error(error?.message || "Unable to load pricing currency view");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [selectedCode]);

  const applyFilters = async (event) => {
    event.preventDefault();
    if (!filters.start_date || !filters.end_date) {
      toast.error("Select both pricing dates");
      return;
    }
    setRefreshing(true);
    try {
      setAppliedFilters(filters);
      await loadPricing(selectedCode, targetCurrency, filters, 1);
    } catch (error) {
      toast.error(error?.message || "Unable to load pricing items");
    } finally {
      setRefreshing(false);
    }
  };

  const changePage = async (page) => {
    if (!page || page === pricing.meta?.current_page) return;
    setRefreshing(true);
    try {
      await loadPricing(selectedCode, targetCurrency, appliedFilters, page);
    } catch (error) {
      toast.error(error?.message || "Unable to load pricing items");
    } finally {
      setRefreshing(false);
    }
  };

  const rates = pricing.rates || [];
  const currencies = settings.currencies || [];
  const target = pricing.target_currency || targetCurrency || settings.currency_code || "NGN";

  if (loading) return <div className="rounded-2xl bg-white p-8 text-slate-600 shadow-sm">Loading pricing currency view…</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <Link to="/dashboard/currency" className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-800"><ArrowLeftIcon className="h-4 w-4" />Currency settings</Link>
          <p className="text-sm font-bold uppercase tracking-wider text-indigo-600">Pricing controls</p>
          <h1 className="mt-1 text-3xl font-bold text-slate-900">Pricing & converted rates</h1>
          <p className="mt-2 max-w-3xl text-slate-600">Review the stored sell price beside its converted value. Conversion is a read-only view and never changes your source pricing.</p>
        </div>
        <PropertyContextSelector properties={properties} value={selectedCode} onChange={setSelectedCode} />
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <form onSubmit={applyFilters} className="grid gap-4 lg:grid-cols-[1fr_1fr_1fr_auto] lg:items-end">
          <label className="text-sm font-semibold text-slate-700">Target currency<select value={targetCurrency} onChange={(event) => setTargetCurrency(event.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-medium"><option value="">Select currency</option>{currencies.map((item) => <option key={item.code} value={item.code}>{item.code} — {item.name}</option>)}</select></label>
          <label className="text-sm font-semibold text-slate-700">From date<input type="date" value={filters.start_date} onChange={(event) => setFilters({ ...filters, start_date: event.target.value })} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 font-medium" /></label>
          <label className="text-sm font-semibold text-slate-700">To date<input type="date" value={filters.end_date} onChange={(event) => setFilters({ ...filters, end_date: event.target.value })} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 font-medium" /></label>
          <button type="submit" disabled={refreshing} className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 font-bold text-white disabled:opacity-50"><RefreshIcon className="h-4 w-4" />{refreshing ? "Loading…" : "Apply filters"}</button>
        </form>
      </section>

      <div className="grid gap-4 sm:grid-cols-3">
        <SummaryCard label="Pricing items" value={pricing.meta?.total || 0} icon={<CurrencyDollarIcon className="h-5 w-5" />} />
        <SummaryCard label="Stored currency" value={pricing.property_currency || settings.currency_code || "—"} icon={<CalendarIcon className="h-5 w-5" />} />
        <SummaryCard label="Conversion target" value={target} icon={<CurrencyDollarIcon className="h-5 w-5" />} />
      </div>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col justify-between gap-2 border-b border-slate-100 px-5 py-4 md:flex-row md:items-center"><div><h2 className="font-bold text-slate-900">Rate price matrix</h2><p className="text-sm text-slate-500">Base values are stored prices. The second line is the converted amount using the selected property rate and hedge.</p></div><span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-700">{pricing.date_range?.start || appliedFilters.start_date} → {pricing.date_range?.end || appliedFilters.end_date}</span></div>
        {rates.length === 0 ? <p className="m-5 rounded-xl bg-slate-50 p-6 text-sm text-slate-500">No pricing items overlap the selected date range.</p> : <div className="overflow-x-auto"><table className="w-full min-w-[1050px] text-left text-sm"><thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-5 py-3">Rate / room</th><th className="px-4 py-3">Validity</th><th className="px-4 py-3">Base price</th><th className="px-4 py-3">Single occupancy</th><th className="px-4 py-3">Double occupancy</th><th className="px-4 py-3">Extra adult</th><th className="px-4 py-3">Conversion</th></tr></thead><tbody className="divide-y divide-slate-100">{rates.map((item) => <tr key={item.id} className="align-top hover:bg-slate-50/70"><td className="px-5 py-4"><p className="font-bold text-slate-800">{item.rate_name || item.rate_code || "Rate"}</p><p className="text-xs text-slate-500">{item.rate_code || "—"} · {item.room_type_name || item.room_type_code || "Room type"}</p></td><td className="whitespace-nowrap px-4 py-4 text-xs text-slate-600">{item.start_date}<br /><span className="text-slate-400">to</span><br />{item.end_date}</td><PriceCell value={item.base_price} converted={item.converted_base_price} from={item.source_currency} to={target} /><PriceCell value={item.single_occupancy} converted={item.converted_single_occupancy} from={item.source_currency} to={target} /><PriceCell value={item.double_occupancy} converted={item.converted_double_occupancy} from={item.source_currency} to={target} /><PriceCell value={item.extra_adult} converted={item.converted_extra_adult} from={item.source_currency} to={target} /><td className="px-4 py-4 text-xs">{item.conversion_error ? <span className="font-semibold text-rose-600">{item.conversion_error}</span> : <><p className="font-semibold text-slate-700">{item.source_currency} → {target}</p><p className="text-slate-500">Effective {item.conversion?.effective_rate ?? "—"}</p><p className="text-slate-400">{item.conversion?.rate_source || "same currency"}{item.conversion?.hedge_value ? ` · +${item.conversion.hedge_value}${item.conversion.hedge_type === "percentage" ? "%" : ""} hedge` : ""}</p></>}</td></tr>)}</tbody></table></div>}
        {pricing.meta?.last_page > 1 && <div className="flex items-center justify-between border-t border-slate-100 px-5 py-4 text-sm"><span className="text-slate-500">Showing {pricing.meta.from || 0}–{pricing.meta.to || 0} of {pricing.meta.total || 0}</span><div className="flex items-center gap-2"><button type="button" disabled={!pricing.meta.current_page || pricing.meta.current_page <= 1 || refreshing} onClick={() => changePage(pricing.meta.current_page - 1)} className="rounded-lg border border-slate-200 px-3 py-1.5 font-semibold text-slate-700 disabled:opacity-40">Previous</button><span className="rounded-lg bg-slate-100 px-3 py-1.5 font-semibold text-slate-700">{pricing.meta.current_page} / {pricing.meta.last_page}</span><button type="button" disabled={pricing.meta.current_page >= pricing.meta.last_page || refreshing} onClick={() => changePage(pricing.meta.current_page + 1)} className="rounded-lg border border-slate-200 px-3 py-1.5 font-semibold text-slate-700 disabled:opacity-40">Next</button></div></div>}
      </section>
    </div>
  );
};

const PriceCell = ({ value, converted, from, to }) => <td className="whitespace-nowrap px-4 py-4"><p className="font-semibold text-slate-800">{money(value, from)}</p><p className="mt-1 text-xs font-semibold text-indigo-600">→ {money(converted, to)}</p></td>;
const SummaryCard = ({ label, value, icon }) => <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"><div className="mb-2 flex items-center gap-2 text-indigo-600">{icon}<span className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</span></div><p className="text-2xl font-bold text-slate-900">{value}</p></div>;

export default CurrencyPricingPage;
